import type { AppMetadata } from '../types/api';

export const APP_METADATA_FRESH_MS = 12 * 60 * 60 * 1000;

export interface DisplayUpcomingEventCounts {
  total: number;
  inPerson: number;
  virtual: number;
  hybrid: number;
}

function nonNegativeNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
}

export function isAppMetadata(value: unknown): value is AppMetadata {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<AppMetadata>;
  return typeof candidate.builtAt === 'string'
    && Number.isFinite(Date.parse(candidate.builtAt))
    && Number.isFinite(candidate.totalUpcomingInPersonEvents)
    && Number.isFinite(candidate.totalUpcomingVirtualEvents)
    && Number.isFinite(candidate.eventsAddedPastWeek);
}

export function getUpcomingEventCounts(metadata: AppMetadata): DisplayUpcomingEventCounts {
  const formats = metadata.upcomingEvents?.byFormat;
  if (formats) {
    const inPerson = nonNegativeNumber(formats.inPerson);
    const virtual = nonNegativeNumber(formats.virtual);
    const hybrid = nonNegativeNumber(formats.hybrid);
    return { total: inPerson + virtual + hybrid, inPerson, virtual, hybrid };
  }

  const inPerson = nonNegativeNumber(metadata.totalUpcomingInPersonEvents);
  const virtual = nonNegativeNumber(metadata.totalUpcomingVirtualEvents);
  return { total: inPerson + virtual, inPerson, virtual, hybrid: 0 };
}

export function isAppMetadataFresh(
  builtAt: string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!builtAt) return false;
  const timestamp = Date.parse(builtAt);
  return Number.isFinite(timestamp)
    && timestamp <= now
    && now - timestamp <= APP_METADATA_FRESH_MS;
}

export function formatCompactCount(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}